import { ApplicationConfig, provideBrowserGlobalErrorListeners, inject, provideZoneChangeDetection, provideAppInitializer, LOCALE_ID, Injectable, importProvidersFrom } from '@angular/core';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient } from '@angular/common/http';
import { provideMessages } from 'messages';
import { provideTest } from 'test';
import { provideDashboard } from 'dashboard';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, translocoConfig, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoLocale, TranslocoLocaleService } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { cookiesStorage, provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { CapabilitiesService, DynamicRouteService, DynamicSidenavService, provideCore, ThemeService } from 'core';
import { baseAuthenticatedRoute, baseRoutes, defaultRoute } from './routes/baseRoutes';
import { BaseLayoutComponent } from './components/base-layout/base-layout';
import { provideTasks } from 'tasks';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ItMatPaginatorIntl } from './utils/paginator-intl';
import { BaseSidenavItemComponent } from './components/base-sidenav-item/base-sidenav-item.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { environment } from '../environments/environment';
import { provideCustomers } from 'customers';
import { PublicClientApplication, InteractionType, BrowserCacheLocation } from '@azure/msal-browser';
import { MsalModule, MsalService } from '@azure/msal-angular';
import { forkJoin, tap } from 'rxjs';
import { provideProducts } from 'products';
import { providePricelists } from 'pricelists';
import { providereceipts } from 'receipts';

export const appConfig: ApplicationConfig = {
  providers: [
    {provide: MAT_DATE_LOCALE, useValue: 'it-IT'},
    provideLuxonDateAdapter(),
    provideBrowserGlobalErrorListeners(),
    //provideZonelessChangeDetection(),
    provideZoneChangeDetection({ eventCoalescing: true}),
    provideRouter(routes,
      withPreloading(PreloadAllModules),
    ),

    provideHttpClient(),
    provideCore({ 
      environmentInfo: environment,
      baseRoutes: baseRoutes,
      baseAuthenticatedRoute: baseAuthenticatedRoute,
      defaultRoute: defaultRoute,
      baseComponent: () => BaseLayoutComponent
    }),
    provideAppInitializer(() => {
      inject(ThemeService); // devo iniettare il servizio dei temi, se no in apertura non carica quello in cache
    }),
    provideAppInitializer(() => {
      const key = environment.mapsApiKey;
      if (!key) return;
      type G = { v: string; key: string };
      (function(g: G) {
        type Win = typeof window & { google?: { maps?: { importLibrary?: unknown; __ib__?: unknown } } };
        const c = 'google', l = 'importLibrary', q = '__ib__';
        const b = window as Win;
        b[c] = b[c] || {} as NonNullable<Win['google']>;
        const bGoogle = b[c]!;
        const d = (bGoogle.maps = bGoogle.maps || {}) as Record<string, unknown>;
        const r = new Set<string>();
        const e = new URLSearchParams();
        let h: Promise<void> | undefined;
        const u = () => h || (h = new Promise<void>(async (f, n) => {
          const a = document.createElement('script');
          e.set('libraries', [...r].join(','));
          (Object.keys(g) as (keyof G)[]).forEach(k =>
            e.set((k as string).replace(/[A-Z]/g, t => '_' + t[0].toLowerCase()), g[k])
          );
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          (d as Record<string, unknown>)[q] = f;
          a.onerror = () => { h = undefined; n(new Error('The Google Maps JavaScript API could not load.')); };
          a.nonce = (document.querySelector('script[nonce]') as HTMLScriptElement)?.nonce || '';
          document.head.appendChild(a);
        }));
        if (d[l]) {
          console.warn('The Google Maps JavaScript API only loads once. Ignoring:', g);
        } else {
          (d as Record<string, unknown>)[l] = (f: string, ...n: unknown[]) => r.add(f) && u().then(() => (d[l] as Function)(f, ...n));
        }
      })({ v: 'weekly', key });
    }),

    //TODO: da verificare e generalizzare nel caso di piu lingue!
    // Material Date Adapter
    // {
    //   provide: DateAdapter,
    //   useClass: NativeDateAdapter,
    // },
    // { //Test only it
    //   provide : MAT_DATE_FORMATS,
    //     useValue: {
    //       dateInput: "dd/MM/yyyy",
    //       display: {
    //           dateInput: "dd/MM/yyyy",
    //           monthYearLabel: "LLLL yyyy",
    //           dateA11yLabel: "dd/MM/yyyy",
    //           monthYearA11yLabel: "MM yyyy",
    //       },
    //     },
    // },
    // { provide: LOCALE_ID, useValue: 'it-IT' },
    // { provide: LOCALE_ID, useFactory: () => { 
    //    //TODO: per gestione dinamica leggere da localstorage la lingua corrente
    //    //TODO: necessita di egisterLocaleData(localeIt); e registerLocaleData(localeEn); in cima
    // }},

    // Matpaginator traslations
    { provide: MatPaginatorIntl, useClass: ItMatPaginatorIntl }, // TODO: gestire caso di altre lingue!

    provideTransloco({
      config: translocoConfig({
        availableLangs: ['it-IT', 'en-US', 'en-GB', 'fr-FR'],
        defaultLang: 'it-IT',
        fallbackLang: 'it-IT',
        reRenderOnLangChange: true,
        prodMode: false,
        missingHandler: {
          useFallbackTranslation: true
        },
        scopes: {
          keepCasing: false
        }
      }),
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoLocale({
      langToLocaleMapping: {
        'en-US': 'en-US',
        'en-GB': 'en-GB',
        'it-IT': 'it-IT',
        'fr-FR': 'fr-FR',
      }
    }),
    provideTranslocoMessageformat({
      locales: ['en-Us', 'en-GB', 'it-IT', 'fr-FR']
    }),
    provideTranslocoPersistLang({
      storage: { useValue: cookiesStorage() },

      getLangFn: ({ cachedLang, browserLang, cultureLang, defaultLang }) => {
        const supported = new Set(['it-IT', 'en-US', 'en-GB', 'fr-FR']);

        // 1) se avevo già settato una lingua ed è in cache, uso quella
        if (cachedLang && supported.has(cachedLang)) return cachedLang;

        // 2) se non ho una lingua in cache, seleziono quella del browser insieme alla cultura
        if (browserLang && supported.has(browserLang)) return browserLang;
        if (cultureLang && supported.has(cultureLang)) return cultureLang;

        // 3) fallback per sicurezza dove uso la lingua di default
        if (defaultLang && supported.has(defaultLang)) return defaultLang;

        return 'it-IT'
      }
    }),
   
    provideMessages(),
    provideTest(),
    provideDashboard(),
    provideTasks(),
    provideCustomers(),
    provideProducts(),
    providePricelists(),
    providereceipts(),

    provideAppInitializer(() => {
      // Caricate qui per gestire i click da link esterni
      const capabilitiesService = inject(CapabilitiesService);
      const dynamicRouteService = inject(DynamicRouteService);
      const dynamicSidenavService = inject(DynamicSidenavService);
      return forkJoin({
        capabilities: capabilitiesService.load(),
        routes: dynamicRouteService.loadRoutes(),
        sidenav: dynamicSidenavService.loadSidenav(),
      }).pipe(
        tap(() => console.log('App Initializer: Capabilities, rotte e sidenav caricate!'))
      );
    }),

    { provide: BaseSidenavItemComponent.getToken(), multi: true, useClass: BaseSidenavItemComponent },

    // entraId (TODO: configurazione da terminare!)
    importProvidersFrom(
      MsalModule.forRoot(
        new PublicClientApplication({
          auth: {
            clientId: 'IL_TUO_CLIENT_ID', // TODO: inserire quello corretto, dovrebbe trovarsi sul portale Azure
            authority: 'https://login.microsoftonline.com/common', // TODO: capire quale usare per prod (credo serva un tenant id)
            redirectUri: 'http://localhost:4200', // TODO: url per sviluppo, capire quale usare per prod: read da url di amiente
            postLogoutRedirectUri: '' // dove andare dopo il logout, puntare a login ?!
          },
          cache: { 
            cacheLocation: BrowserCacheLocation.LocalStorage,
          }
        }),
        {
          interactionType: InteractionType.Redirect,
          authRequest: { scopes: ['user.read'] }
        },
        {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: new Map([ // serve per aggiunta token. valutare se utile avendo interceptor custom
            ['https://graph.microsoft.com/v1.0/me', ['user.read']]
          ])
        }
      )
    ),
    MsalService
  ]
};

